import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { PaymentBankAccount } from '../../models/entities/payment-bank-account';
import { CreditCard } from '../../models/entities/credit-card';
import { LobBankAccount } from '../../models/entities/lob-bank-account';
import { map } from 'rxjs/operators';
import { GtmService, PaymentTypes } from '../gtm.service';

export interface UserPaymentMethodsResponse
{
    paymentBankAccounts: PaymentBankAccount[];
    creditCards: CreditCard[];
    billPayAccounts: LobBankAccount[];
}

export interface OrganizationPaymentMethodsResponse
{
    paymentBankAccounts: PaymentBankAccount[];
    creditCards: CreditCard[];
}

@Injectable({
    providedIn: 'root',
})
export class PaymentMethodsService
{
    constructor(
            private _http: ApplicationHttpClient,
            private _gtm: GtmService,
    ) {
    }

    /**
     * Get all of the specified user's payment methods.
     * @param {number} userId
     * @returns {any}
     */
    public getUserPaymentMethods(userId: number): Observable<UserPaymentMethodsResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UserPaymentMethods(userId || jwt.id));
        return this._http.get(url).pipe(map((response: Object) => {
            return {
                paymentBankAccounts: (response['bankAccounts'] || []).map(account => new PaymentBankAccount(account)),
                creditCards: (response['creditCards'] || []).map(card => new CreditCard(card)),
                billPayAccounts: (response['billPayAccounts'] || []).map(account => new LobBankAccount(account)),
            };
        }));
    }

    /**
     * Get all of the specified organization's payment methods.
     * @param {number} organizationId
     * @returns {Observable}
     */
    public getOrgPaymentMethods(organizationId: number): Observable<OrganizationPaymentMethodsResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.OrgPaymentMethods(organizationId || jwt.orgId));
        return this._http.get(url).pipe(map((response: Object) => {
            return {
                paymentBankAccounts: (response['bankAccounts'] || []).map(account => new PaymentBankAccount(account)),
                creditCards: (response['creditCards'] || []).map(card => new CreditCard(card)),
            };
        })).pipe(map((response: OrganizationPaymentMethodsResponse) => {
            response.paymentBankAccounts.forEach((account: PaymentBankAccount) => {
                if (account.fresh) {
                    this._gtm.addPaymentMethod({
                        type: PaymentTypes.BANK_ACCOUNT,
                        orgId: organizationId,
                        addedBy: jwt.id,
                    });
                }
            });

            response.creditCards.forEach((card: CreditCard) => {
                if (card.fresh) {
                    this._gtm.addPaymentMethod({
                        type: PaymentTypes.CREDIT_CARD,
                        orgId: organizationId,
                        addedBy: jwt.id,
                    });
                }
            });
            return response;
        }));
    }
}
